import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  ArrowDown,
  ArrowUp,
  Building2,
  ChevronLeft,
  Loader2,
  Pencil,
  Save,
  Trash2,
  X,
} from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Switch } from "@/components/ui/switch";

export default function ApplicationEditPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [details, setDetails] = useState({ name: "", description: "", support_team: "" });
  const [newModule, setNewModule] = useState({ name: "", code: "" });
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ name: "", code: "" });
  const [confirmDelete, setConfirmDelete] = useState(null); // module id
  const [confirmDeleteApp, setConfirmDeleteApp] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [clientAccess, setClientAccess] = useState({});
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientUpdatingId, setClientUpdatingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/applications");
    const found = (data || []).find((a) => a.id === appId);
    if (!found) {
      toast.error("Application not found");
      navigate("/admin/applications");
      return;
    }
    setApp(found);
    setDetails({ name: found.name || "", description: found.description || "", support_team: found.support_team || "" });
  };
  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, [appId]);

  useEffect(() => {
    const loadClients = async () => {
      setClientsLoading(true);

      try {
        const organizationResponse = await api.get("/organizations");
        const organizationItems = organizationResponse.data || [];

        setOrganizations(organizationItems);

        const results = await Promise.allSettled(
          organizationItems.map(async (organization) => {
            const response = await api.get(
              `/organizations/${organization.id}/applications`
            );

            const application = (
              response.data?.applications || []
            ).find((item) => item.id === appId);

            return {
              organizationId: organization.id,
              assigned: Boolean(application?.assigned),
              legacyAllAccess: Boolean(
                response.data?.legacy_all_access
              ),
            };
          })
        );

        const access = {};

        results.forEach((result) => {
          if (result.status === "fulfilled") {
            access[result.value.organizationId] = {
              assigned: result.value.assigned,
              legacyAllAccess:
                result.value.legacyAllAccess,
            };
          }
        });

        setClientAccess(access);
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setClientsLoading(false);
      }
    };

    loadClients();
  }, [appId]);

  const saveDetails = async () => {
    try {
      await api.patch(`/applications2/${appId}`, details);
      toast.success("Application updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addModule = async () => {
    if (!newModule.name.trim()) return toast.error("Module name required");
    try {
      await api.post(`/applications/${appId}/modules`, newModule);
      setNewModule({ name: "", code: "" });
      toast.success("Module added");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (m) => { setEditingId(m.id); setEditValues({ name: m.name, code: m.code || "" }); };
  const cancelEdit = () => { setEditingId(null); setEditValues({ name: "", code: "" }); };
  const saveEdit = async () => {
    try {
      await api.patch(`/applications/${appId}/modules/${editingId}`, editValues);
      cancelEdit();
      toast.success("Module updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteModule = async (id) => {
    try {
      await api.delete(`/applications/${appId}/modules/${id}`);
      toast.success("Module deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (idx, dir) => {
    const modules = [...(app.modules || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= modules.length) return;
    const [m] = modules.splice(idx, 1);
    modules.splice(newIdx, 0, m);
    try {
      await api.post(`/applications/${appId}/modules/reorder`, { module_ids: modules.map((x) => x.id) });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleClientAccess = async (
    organization,
    enabled
  ) => {
    if (clientUpdatingId) return;

    setClientUpdatingId(organization.id);

    setClientAccess((current) => ({
      ...current,
      [organization.id]: {
        ...(current[organization.id] || {}),
        assigned: enabled,
      },
    }));

    try {
      if (enabled) {
        await api.post(
          `/organizations/${organization.id}/applications/${appId}`
        );
      } else {
        await api.delete(
          `/organizations/${organization.id}/applications/${appId}`
        );
      }

      toast.success(
        enabled
          ? `${app?.name || "Application"} assigned to ${organization.name}`
          : `${app?.name || "Application"} removed from ${organization.name}`
      );
    } catch (error) {
      setClientAccess((current) => ({
        ...current,
        [organization.id]: {
          ...(current[organization.id] || {}),
          assigned: !enabled,
        },
      }));

      toast.error(formatApiError(error));
    } finally {
      setClientUpdatingId(null);
    }
  };

  const deleteApp = async () => {
    try {
      await api.delete(`/applications/${appId}`);
      toast.success("Application deleted (soft delete)");
      navigate("/admin/applications");
    } catch (e) { toast.error(formatApiError(e)); setConfirmDeleteApp(false); }
  };

  if (!app) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-app-edit-page">
      <Link to="/admin/applications" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
        <ChevronLeft className="h-3 w-3" /> Back to Applications
      </Link>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{app.name} <span className="font-mono text-lg text-primary">· {app.code}</span></h1>
        </div>
        <Button data-testid="app-delete" variant="outline" className="text-rose-700 border-rose-500/30 hover:bg-rose-500/10" onClick={() => setConfirmDeleteApp(true)}>Delete Application</Button>
      </div>

      <section className="rounded-xl border border-border bg-card p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Details</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Name</Label><Input data-testid="app-edit-name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} /></div>
          <div><Label>Support team</Label><Input data-testid="app-edit-support" value={details.support_team} onChange={(e) => setDetails({ ...details, support_team: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Description</Label><Textarea data-testid="app-edit-description" rows={2} value={details.description} onChange={(e) => setDetails({ ...details, description: e.target.value })} /></div>
        </div>
        <div className="flex justify-end">
          <Button data-testid="app-edit-save" onClick={saveDetails} className="bg-primary text-primary-foreground hover:bg-primary/90">Save Changes</Button>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Client Access
            </div>

            <h2 className="mt-1 font-display text-lg font-bold">
              Assigned Clients
            </h2>

            <p className="mt-1 text-xs text-muted-foreground">
              Choose which client organizations can use this application.
            </p>
          </div>

          <div className="text-xs text-muted-foreground">
            {
              Object.values(clientAccess).filter(
                (item) => item.assigned
              ).length
            }{" "}
            assigned
          </div>
        </div>

        {clientsLoading ? (
          <div className="py-10 text-center text-xs text-muted-foreground">
            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
            Loading Client Access…
          </div>
        ) : organizations.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <Building2 className="mx-auto h-6 w-6 text-muted-foreground" />

            <div className="mt-2 text-sm font-medium">
              No Clients Available
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {organizations.map((organization) => {
              const access =
                clientAccess[organization.id] || {};

              return (
                <div
                  key={organization.id}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div className="flex min-w-0 items-center gap-3">

                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary/50 text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                    </div>

                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">
                        {organization.name}
                      </div>

                      <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                        {organization.code && (
                          <span>{organization.code}</span>
                        )}

                        {access.legacyAllAccess && (
                          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-amber-500">
                            Legacy All Access
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">

                    {clientUpdatingId ===
                      organization.id && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}

                    <Switch
                      checked={Boolean(access.assigned)}
                      disabled={Boolean(clientUpdatingId)}
                      onCheckedChange={(checked) =>
                        toggleClientAccess(
                          organization,
                          checked
                        )
                      }
                      data-testid={`application-client-${organization.id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-border bg-card p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Modules</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2 items-end">
          <div><Label>New module name</Label><Input data-testid="module-new-name" value={newModule.name} onChange={(e) => setNewModule({ ...newModule, name: e.target.value })} /></div>
          <div><Label>Code (optional)</Label><Input data-testid="module-new-code" value={newModule.code} onChange={(e) => setNewModule({ ...newModule, code: e.target.value })} /></div>
          <Button data-testid="module-add" onClick={addModule} className="bg-primary text-primary-foreground hover:bg-primary/90">Add Module</Button>
        </div>
        <div className="divide-y divide-border">
          {(app.modules || []).map((m, idx) => (
            <div key={m.id} data-testid={`module-row-${m.id}`} className="flex items-center gap-3 py-2">
              <span className="text-xs w-8 font-mono text-muted-foreground">#{idx + 1}</span>
              {editingId === m.id ? (
                <>
                  <Input data-testid={`module-edit-name-${m.id}`} value={editValues.name} onChange={(e) => setEditValues({ ...editValues, name: e.target.value })} className="flex-1" />
                  <Input data-testid={`module-edit-code-${m.id}`} value={editValues.code} onChange={(e) => setEditValues({ ...editValues, code: e.target.value })} className="w-32" />
                  <button data-testid={`module-save-${m.id}`} onClick={saveEdit} className="text-emerald-700"><Save className="h-4 w-4" /></button>
                  <button onClick={cancelEdit}><X className="h-4 w-4" /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm">{m.name}</span>
                  <span className="text-xs font-mono text-muted-foreground w-32">{m.code || "—"}</span>
                  <button data-testid={`module-up-${m.id}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="text-muted-foreground disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button data-testid={`module-down-${m.id}`} disabled={idx === (app.modules?.length || 0) - 1} onClick={() => move(idx, 1)} className="text-muted-foreground disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  <button data-testid={`module-edit-${m.id}`} onClick={() => startEdit(m)} className="text-primary"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`module-delete-${m.id}`} onClick={() => setConfirmDelete(m.id)} className="text-rose-700"><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          ))}
          {(!app.modules || app.modules.length === 0) && <div className="text-xs text-muted-foreground py-3">No modules yet — add one above.</div>}
        </div>
      </section>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete module?"
        description="This is a hard delete but is blocked if the module is linked to open tickets."
        confirmLabel="Delete"
        destructive
        onConfirm={() => deleteModule(confirmDelete)}
      />
      <ConfirmDialog
        open={confirmDeleteApp}
        onOpenChange={setConfirmDeleteApp}
        title={`Delete ${app.name}?`}
        description="Soft delete — the application is hidden but preserved because tickets may reference it. You can restore later."
        confirmLabel="Delete"
        destructive
        onConfirm={deleteApp}
        testid="confirm-delete-app"
      />
    </div>
  );
}
